import React, { useState, useCallback } from 'react';
import { delimited } from '../../../common/utils/commonUtil';
import IncomeModal from './incomeModal';
import AngelTaxResultView from './angelTaxResultView';
import type { AngelTax } from './angelTaxResultView';

type Props = {
  taxReturnImage: string;
  taxWithholdingImage: string;
};

// 株式譲渡益に対する税率(復興特別所得税含む)
const S_TAX = 0.15315;
// 復興特別所得税率
const TAX_FOR_RECONSTRUCTION = 0.021;

function lookUpTaxPercentage(income: number): number {
  if (income <= 1950000) {
    return 0.05;
  } else if (1950000 < income && income <= 3300000) {
    return 0.1;
  } else if (3300000 < income && income <= 6950000) {
    return 0.2;
  } else if (6950000 < income && income <= 9000000) {
    return 0.23;
  } else if (9000000 < income && income <= 18000000) {
    return 0.33;
  } else if (18000000 < income && income <= 40000000) {
    return 0.4;
  } else {
    return 0.45;
  }
}

function calcDeduction(tax: number): number {
  if (tax <= 0.05) {
    return 0;
  } else if (tax === 0.1) {
    return 97500;
  } else if (tax === 0.2) {
    return 427500;
  } else if (tax === 0.23) {
    return 636000;
  } else if (tax === 0.33) {
    return 1536000;
  } else if (tax === 0.4) {
    return 2796000;
  } else if (tax === 0.45) {
    return 4796000;
  } else {
    return 0;
  }
}

export default function AngelTaxView({
  taxReturnImage,
  taxWithholdingImage,
}: Props) {
  const [income, setIncome] = useState('');
  const [incomeError, setIncomeError] = useState<string | null>(null);
  const [investment, setInvestment] = useState('');
  const [investmentError, setInvestmentError] = useState<string | null>(null);
  const [gain, setGain] = useState('0');
  const [gainError, setGainError] = useState<string | null>(null);
  const [gainByOutsideStock, setGainByOutsideStock] = useState('0');
  const [gainByOutsideStockError, setGainByOutsideStockError] = useState<
    string | null
  >(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [incomeTax, setIncomeTax] = useState<number | null>(null);
  const [angelTaxA, setAngelTaxA] = useState<AngelTax | null>(null);
  const [angelTaxB, setAngelTaxB] = useState<AngelTax | null>(null);

  const errorMessageOnChange = useCallback((value?: string): string | null => {
    if (value == null || value === '') {
      return null;
    }
    if (!value.match(/^[0-9]+$/)) {
      return '半角数字を入力してください';
    }
    if (parseInt(value) >= parseInt('1000000000000')) {
      return '1,000,000,000,000未満の値を入力してください';
    }
    return null;
  }, []);

  const numberWithComma = (value: string): string => {
    if (!value.match(/^[0-9]+$/)) {
      return value;
    }
    return delimited(Math.floor(parseInt(value)));
  };

  const deleteComma = (value: string): string => value.replace(/,/g, '');

  const integerWithoutComma = (value: string): number => {
    if (value === '') return 0;
    return parseInt(deleteComma(value));
  };

  const roundForTax = (sourceNumber?: number): number => {
    if (sourceNumber == null) return 0;
    return Math.floor(sourceNumber / 100) * 100;
  };

  const baseDeductionByIncome = (income?: number): number => {
    if (income != null) {
      if (income <= 1320000) {
        return 950000;
      } else if (income <= 3360000) {
        return 880000;
      } else if (income <= 4890000) {
        return 680000;
      } else if (income <= 6550000) {
        return 630000;
      } else if (income <= 23500000) {
        return 580000;
      } else if (income <= 24000000) {
        return 480000;
      } else if (income <= 24500000) {
        return 320000;
      } else if (income <= 25000000) {
        return 160000;
      } else {
        return 0;
      }
    } else {
      return 0;
    }
  };

  const calcIncomeTax = useCallback((): number | null => {
    if (
      income === '' ||
      incomeError != null ||
      gainError != null ||
      gainByOutsideStockError != null
    ) {
      return null;
    }
    const incomeVal = integerWithoutComma(income);
    const gainTotal =
      integerWithoutComma(gain) + integerWithoutComma(gainByOutsideStock);
    const taxPercentage = lookUpTaxPercentage(
      incomeVal - baseDeductionByIncome(incomeVal),
    );
    const deduction = calcDeduction(taxPercentage);
    const taxAfterDeduction = Math.max(
      roundForTax(
        (incomeVal - baseDeductionByIncome(incomeVal)) * taxPercentage -
          deduction,
      ),
      0,
    );
    const wkAmount = taxAfterDeduction * (TAX_FOR_RECONSTRUCTION * 1000);
    const reconstructionTax = wkAmount / 1000;
    return Math.max(
      taxAfterDeduction +
        roundForTax((gainTotal - Math.max(480000 - incomeVal, 0)) * S_TAX) +
        reconstructionTax,
      0,
    );
  }, [
    income,
    incomeError,
    gain,
    gainError,
    gainByOutsideStock,
    gainByOutsideStockError,
  ]);

  const calcAngelTaxA = useCallback(
    (incomeTax: number | null): AngelTax | null => {
      // investment, gain, gainByOutsideStockは空でもOKに変更した。
      // 初期値に0を設定しないようにしたため。
      if (
        incomeTax == null ||
        income === '' ||
        incomeError != null ||
        investmentError != null ||
        gainError != null ||
        gainByOutsideStockError != null
      ) {
        return null;
      }
      const incomeVal = integerWithoutComma(income);
      const investmentVal = integerWithoutComma(investment);
      const gainTotal =
        integerWithoutComma(gain) + integerWithoutComma(gainByOutsideStock);
      const baseDeduction = baseDeductionByIncome(incomeVal);
      // 基礎控除のみ引いて判定用
      const incomeExceptBaseDeduction = Math.max(incomeVal - baseDeduction, 0);
      const deductionA = Math.max(
        Math.min(investmentVal, 8000000, incomeVal * 0.4) - 2000,
        0,
      );
      // 基礎控除＆優遇措置Aを引いて判定用
      const incomeExceptDeductionAandBase = Math.max(
        incomeExceptBaseDeduction - deductionA,
        0,
      );
      // 税率
      const taxPercentage = lookUpTaxPercentage(incomeExceptDeductionAandBase);
      const deduction = calcDeduction(taxPercentage);
      // 譲渡益を除いた所得税(復興税未加算)
      const incomeTaxExceptGailTotal =
        roundForTax(incomeExceptDeductionAandBase * taxPercentage) - deduction;
      const incomeTaxA =
        incomeTaxExceptGailTotal +
        roundForTax((gainTotal - Math.max(480000 - incomeVal, 0)) * S_TAX);
      const reconstructionTax = Math.floor(
        incomeTaxExceptGailTotal * TAX_FOR_RECONSTRUCTION,
      );
      return {
        deduction: deductionA,
        incomeTax: incomeTaxA + reconstructionTax,
        refund: Math.max(incomeTax - (incomeTaxA + reconstructionTax), 0),
      };
    },
    [
      income,
      incomeError,
      investment,
      investmentError,
      gain,
      gainError,
      gainByOutsideStock,
      gainByOutsideStockError,
    ],
  );

  const calcAngelTaxB = useCallback(
    (incomeTax: number | null): AngelTax | null => {
      // investment, gain, gainByOutsideStockは空でもOKに変更した。
      // 初期値に0を設定しないようにしたため。
      if (
        incomeTax == null ||
        income === '' ||
        incomeError != null ||
        investmentError != null ||
        gainError != null ||
        gainByOutsideStockError != null
      ) {
        return null;
      }
      const incomeVal = integerWithoutComma(income);
      const investmentVal = integerWithoutComma(investment);
      const gainTotal =
        integerWithoutComma(gain) + integerWithoutComma(gainByOutsideStock);
      const baseDeduction = baseDeductionByIncome(incomeVal);
      // 基礎控除のみ引いて判定用
      const incomeExceptBaseDeduction = Math.max(incomeVal - baseDeduction, 0);
      // 税率
      const taxPercentage = lookUpTaxPercentage(incomeExceptBaseDeduction);
      const deduction = calcDeduction(taxPercentage);
      const deductionB = Math.min(
        Math.min(investmentVal, gainTotal) +
          (incomeVal <= 480000 ? 480000 - incomeVal : 0),
        gainTotal,
      );
      const incomeTaxExceptGailTotal =
        roundForTax(incomeExceptBaseDeduction * taxPercentage) - deduction;
      const reconstructionTax = Math.floor(
        incomeTaxExceptGailTotal * TAX_FOR_RECONSTRUCTION,
      );
      const incomeTaxB =
        incomeTaxExceptGailTotal +
        roundForTax(
          Math.max(
            gainTotal - investmentVal - Math.max(480000 - incomeVal, 0),
            0,
          ) * S_TAX,
        ) +
        reconstructionTax;
      return {
        deduction: deductionB,
        incomeTax: incomeTaxB,
        refund: Math.max(incomeTax - incomeTaxB, 0),
      };
    },
    [
      income,
      incomeError,
      investment,
      investmentError,
      gain,
      gainError,
      gainByOutsideStock,
      gainByOutsideStockError,
    ],
  );

  const handleSubmit = () => {
    const tax = calcIncomeTax();
    setShowResult(true);
    setIncomeTax(tax);
    setAngelTaxA(calcAngelTaxA(tax));
    setAngelTaxB(calcAngelTaxB(tax));
  };

  return (
    <div className="angel-main-wrap">
      <h1 className="angel-title">エンジェル税制シミュレーター</h1>
      <div className="alert alert-primary alert-dismissible angel-alert-text">
        <b>所得税の計算について</b>
        <ul>
          <li>
            令和7年12月時点の税制を基にした概算のシミュレーションとなります。
          </li>
          <li>
            所得控除は基礎控除のみで、その他控除については考慮していません。
          </li>
          <li>所得税額には、復興特別所得税額(2.1%)が含まれています。</li>
          <li>
            株式譲渡は分離課税選択で税率は15.315%(所得税および復興特別所得税)です。
          </li>
          <li>
            基礎控除の計算については、総所得金額と合計所得金額が同一である前提で行っています。
          </li>
        </ul>
      </div>
      <div>
        <form className="top-form">
          <div
            className={
              incomeError != null
                ? 'angel-form-input-block form-group income has-error'
                : 'angel-form-input-block form-group income'
            }
          >
            <label
              className="control-label sp-none required-label income-label"
              htmlFor="income"
            >
              総所得金額
              <div
                className="question-icon"
                onClick={() => setShowIncomeModal(true)}
              ></div>
            </label>

            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="income"
                  type="text"
                  inputMode="numeric"
                  className={
                    income !== '' && incomeError === null
                      ? 'form-control required is-valid'
                      : 'form-control required'
                  }
                  value={income}
                  onChange={e => {
                    setIncome(e.target.value);
                    setIncomeError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setIncome(value);
                    setIncomeError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setIncome(numberWithComma(e.target.value));
                    setIncomeError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 10000000"
                />
                <span>円</span>
              </div>
              {incomeError && (
                <span className="help-block error-message">{incomeError}</span>
              )}
            </div>
          </div>
          <div
            className={
              investmentError != null
                ? 'angel-form-input-block form-group investment has-error'
                : 'angel-form-input-block form-group investment'
            }
          >
            <label
              className="control-label required-label"
              htmlFor="investment"
            >
              エンジェル税制適用会社への投資額
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="investment"
                  type="text"
                  inputMode="numeric"
                  className={
                    investment !== '' && investmentError === null
                      ? 'form-control required is-valid'
                      : 'form-control required'
                  }
                  value={investment}
                  onChange={e => {
                    setInvestment(e.target.value);
                    setInvestmentError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setInvestment(value);
                    setInvestmentError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setInvestment(numberWithComma(e.target.value));
                    setInvestmentError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 500000"
                />
                <span>円</span>
              </div>
              {investmentError && (
                <span className="help-block error-message">
                  {investmentError}
                </span>
              )}
            </div>
          </div>
          <div
            className={
              gainError != null
                ? 'angel-form-input-block form-group gain has-error'
                : 'angel-form-input-block form-group gain'
            }
          >
            <label className="control-label optional-label" htmlFor="gain">
              上場株式の売買による譲渡益
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="gain"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={gain}
                  onChange={e => {
                    setGain(e.target.value);
                    setGainError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setGain(value);
                    setGainError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setGain(numberWithComma(e.target.value));
                    setGainError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 1000000"
                />
                <span>円</span>
              </div>
              {gainError && (
                <span className="help-block error-message">{gainError}</span>
              )}
              <p className="help-block pt-2 font-weight-normal">
                ※上場株式の売買において譲渡損の場合は0を記入してください。
                <br />
                ※上場株式に関わる譲渡損失の繰越がない前提で記入してください。
                <br />
                ※株式譲渡は特定口座（源泉あり）で取引されていることが前提です。
              </p>
            </div>
          </div>
          <div
            className={
              gainByOutsideStockError != null
                ? 'angel-form-input-block form-group gain-by-outside-stock has-error'
                : 'angel-form-input-block form-group gain-by-outside-stock'
            }
          >
            <label
              className="control-label optional-label"
              htmlFor="gain-by-outside-stock"
            >
              非上場株式の売買による譲渡益
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="gain-by-outside-stock"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={gainByOutsideStock}
                  onChange={e => {
                    setGainByOutsideStock(e.target.value);
                    setGainByOutsideStockError(
                      errorMessageOnChange(e.target.value),
                    );
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setGainByOutsideStock(value);
                    setGainByOutsideStockError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setGainByOutsideStock(numberWithComma(e.target.value));
                    setGainByOutsideStockError(
                      errorMessageOnChange(e.target.value),
                    );
                  }}
                  placeholder="例, 1000000"
                />
                <span>円</span>
              </div>
              {gainByOutsideStockError && (
                <span className="help-block error-message">
                  {gainByOutsideStockError}
                </span>
              )}
              <p className="help-block pt-2 font-weight-normal">
                ※非上場株式の売買において譲渡損の場合は0を記入してください。
                <br />
                ※非上場株式に関わる譲渡損失の繰越がない前提で記入してください。
              </p>
            </div>
          </div>
        </form>

        <div
          className={
            showResult ? 'calculation-submit recalculate' : 'calculation-submit'
          }
          onClick={() => handleSubmit()}
        >
          <a>{showResult ? '再計算する' : '計算する'}</a>
        </div>
      </div>

      <AngelTaxResultView
        showResult={showResult}
        incomeTax={incomeTax}
        angelTaxA={angelTaxA}
        angelTaxB={angelTaxB}
      />

      <IncomeModal
        show={showIncomeModal}
        taxReturnImage={taxReturnImage}
        taxWithholdingImage={taxWithholdingImage}
        onClose={() => setShowIncomeModal(false)}
      />
      <div className="angel-tax__cta">
        <div className="angel-tax__cta__inner">
          <div className="angel-tax__cta__text">
            最短３分！WEBで完結！
            <br />
            登録後、新しい案件情報をお届けします。
          </div>
          <a href="/users/sign_up" className="angel-tax__cta__btn">
            無料で投資家登録
          </a>
        </div>
      </div>
    </div>
  );
}
